import axios from '../anaxios';

const VERSION_RELOAD_KEY = 'bookem_last_version_reload';
const RELOAD_LOOP_THRESHOLD_MS = 30000; // 30 seconds
const SERVICE_WORKER_LOOKUP_TIMEOUT_MS = 1500;
const SERVICE_WORKER_UPDATE_TIMEOUT_MS = 5000;
const SERVICE_WORKER_UNREGISTER_TIMEOUT_MS = 10000;
const SERVICE_WORKER_UNREGISTER_ATTEMPTS = 2;
const FORCE_RELOAD_CHANNEL = 'bookem-update';
const FORCE_RELOAD_STORAGE_KEY = 'bookem_force_reload';
export const VERSION_CHECK_INTERVAL_MS = 15 * 60 * 1000;
export const VERSION_CHECK_STALE_MS = 5 * 60 * 1000;
export const UPDATE_MODAL_DELAY_MS = 30 * 60 * 1000;
const STALE_ASSET_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading chunk .+ failed/i,
  /ChunkLoadError/i,
  /Unable to preload CSS/i,
];

let pwaUpdateReady = false;
let lastVersionCheckAt = 0;
let versionCheckPromise: Promise<boolean> | null = null;
let lastReloadAt: number | null = null;
let reloadRetryTimeout: ReturnType<typeof setTimeout> | null = null;

type ForceReloadMessage = {
  type: 'force-reload';
  id: string;
};

const withTimeout = /* oxfmt-ignore */ <T,>(
  promise: Promise<T>,
  timeoutMs: number
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return Promise.race<T | null>([
    promise,
    new Promise<null>((resolve) => {
      timeoutId = setTimeout(() => resolve(null), timeoutMs);
    }),
  ])
    .catch(() => null)
    .finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
};

const getServiceWorkerRegistrations = async (
  preferredRegistration?: ServiceWorkerRegistration | null,
): Promise<ServiceWorkerRegistration[]> => {
  if (preferredRegistration) {
    return [preferredRegistration];
  }

  if (!('serviceWorker' in navigator)) {
    return [];
  }

  const registrations = await withTimeout(
    navigator.serviceWorker.getRegistrations(),
    SERVICE_WORKER_LOOKUP_TIMEOUT_MS,
  );

  if (registrations?.length) {
    return registrations;
  }

  const registration = await withTimeout(
    navigator.serviceWorker.getRegistration(),
    SERVICE_WORKER_LOOKUP_TIMEOUT_MS,
  );

  return registration ? [registration] : [];
};

export const updateServiceWorkerRegistrations = async (
  preferredRegistration?: ServiceWorkerRegistration | null,
): Promise<void> => {
  const registrations = await getServiceWorkerRegistrations(
    preferredRegistration,
  );

  await Promise.allSettled(
    registrations.map((registration) =>
      withTimeout(registration.update(), SERVICE_WORKER_UPDATE_TIMEOUT_MS),
    ),
  );
};

const unregisterServiceWorkers = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  for (
    let attempt = 0;
    attempt < SERVICE_WORKER_UNREGISTER_ATTEMPTS;
    attempt++
  ) {
    let expired = false;
    const unregistered = await withTimeout(
      (async () => {
        // Lookup failures must not be treated as an empty registration list.
        const registrations = await navigator.serviceWorker.getRegistrations();
        // A timeout cannot cancel the browser API. Ignore a late lookup so it
        // cannot unregister a new worker after a subsequent attempt succeeds.
        if (expired) {
          return false;
        }
        await Promise.all(
          registrations.map((registration) => registration.unregister()),
        );
        // false from unregister() means the registration was already removed.
        return true;
      })(),
      SERVICE_WORKER_UNREGISTER_TIMEOUT_MS,
    );
    expired = true;

    if (unregistered) {
      return;
    }
  }

  throw new Error('Unable to unregister service workers after two attempts');
};

const createForceReloadMessage = (): ForceReloadMessage => ({
  type: 'force-reload',
  id: `${Date.now()}-${Math.random()}`,
});

const isForceReloadMessage = (value: unknown): value is ForceReloadMessage => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    value.type === 'force-reload'
  );
};

export const broadcastForceReload = (): void => {
  const message = createForceReloadMessage();

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel(FORCE_RELOAD_CHANNEL);
      channel.postMessage(message);
      channel.close();
    } catch {
      // Ignore browsers where BroadcastChannel exists but is unusable.
    }
  }

  try {
    localStorage.setItem(FORCE_RELOAD_STORAGE_KEY, JSON.stringify(message));
  } catch {
    // Ignore private-mode and storage-disabled browsers.
  }
};

export const listenForForceReload = (onReload: () => void): (() => void) => {
  let channel: BroadcastChannel | null = null;

  const handleMessage = (event: MessageEvent) => {
    if (isForceReloadMessage(event.data)) {
      onReload();
    }
  };

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel(FORCE_RELOAD_CHANNEL);
      channel.addEventListener('message', handleMessage);
    } catch {
      channel = null;
    }
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== FORCE_RELOAD_STORAGE_KEY || !event.newValue) {
      return;
    }

    try {
      if (isForceReloadMessage(JSON.parse(event.newValue))) {
        onReload();
      }
    } catch {
      // Ignore malformed values from manual storage edits.
    }
  };

  window.addEventListener('storage', handleStorage);

  return () => {
    channel?.removeEventListener('message', handleMessage);
    channel?.close();
    window.removeEventListener('storage', handleStorage);
  };
};

export const forceReloadApp = async (): Promise<void> => {
  await unregisterServiceWorkers();
  broadcastForceReload();
  window.location.reload();
};

export const markPwaUpdateReady = (): void => {
  pwaUpdateReady = true;
};

export const isPwaUpdateReady = (): boolean => pwaUpdateReady;

export const shouldCheckForNewVersion = (
  staleAfterMs = VERSION_CHECK_STALE_MS,
): boolean => {
  if (__TARGET_ENV__ === 'dev') {
    return false;
  }

  return Date.now() - lastVersionCheckAt >= staleAfterMs;
};

/**
 * Checks if a new version of the app is available by comparing
 * the current version hash with the one from version.json
 * @param basePath - Optional base path for the version.json URL (e.g., __PUBLIC_PATH__)
 */
export const hasNewVersion = (basePath = ''): Promise<boolean> => {
  if (__TARGET_ENV__ === 'dev') {
    return Promise.resolve(false);
  }
  return axios
    .get(`${basePath}/version.json`)
    .then(({ data }) => data.hash && __VERSION__ !== data.hash);
};

export const checkForNewVersion = (basePath = ''): Promise<boolean> => {
  if (versionCheckPromise) {
    return versionCheckPromise;
  }

  lastVersionCheckAt = Date.now();
  versionCheckPromise = hasNewVersion(basePath).finally(() => {
    versionCheckPromise = null;
  });
  return versionCheckPromise;
};

export const isStaleAssetLoadError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  const errorObject = error as {
    message?: unknown;
    name?: unknown;
    stack?: unknown;
  };

  const message = [
    typeof error === 'string' ? error : '',
    typeof errorObject.name === 'string' ? errorObject.name : '',
    typeof errorObject.message === 'string' ? errorObject.message : '',
    typeof errorObject.stack === 'string' ? errorObject.stack : '',
  ].join('\n');

  return STALE_ASSET_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

/**
 * Keep the cooldown per tab and running build, including across reloads.
 * A blocked request gets one deferred retry instead of silently being lost.
 */
export const safeReloadForNewVersion = (): boolean => {
  const key = `${VERSION_RELOAD_KEY}:${__VERSION__}`;
  const now = Date.now();
  try {
    const stored = sessionStorage.getItem(key);
    const timestamp = stored === null ? NaN : Number(stored);
    if (Number.isFinite(timestamp)) {
      lastReloadAt = timestamp;
    }
  } catch {
    // Storage can be disabled; retain the in-memory cooldown in this document.
  }

  const elapsed = lastReloadAt === null ? Infinity : now - lastReloadAt;
  if (elapsed >= 0 && elapsed < RELOAD_LOOP_THRESHOLD_MS) {
    if (reloadRetryTimeout === null) {
      reloadRetryTimeout = setTimeout(() => {
        reloadRetryTimeout = null;
        safeReloadForNewVersion();
      }, RELOAD_LOOP_THRESHOLD_MS - elapsed);
    }
    return false;
  }

  if (reloadRetryTimeout !== null) {
    clearTimeout(reloadRetryTimeout);
    reloadRetryTimeout = null;
  }
  lastReloadAt = now;
  try {
    sessionStorage.setItem(key, now.toString());
  } catch {
    // Updating must still work when session storage is unavailable.
  }
  window.location.reload();
  return true;
};
